import Vue from "vue"
import joinUrl from "url-join"
import ApiClient from "@/lib/api-client"
import fetchWithRetry from "@/lib/fetch-with-retry"
import { isPresent } from "@/lib/utils"

import { findProgressRoot, findAccessRoot } from "@/lib/access/utils"
import { ROOT_TREE_ID } from "@/lib/page-utils"
import { MVT_EVENTS } from "@/sdk/event-manager"
import {
  getCachedPage,
  cacheAccessTree,
  getCachedAccessTree
} from "@/lib/page-cache"

const LOAD_RETRY_LIMIT = 100 // Max 10 seconds w/ 100ms delay

// Progress is one concept over two subjects, mirroring Page::Progress#subject.
// The ref names the subject; the row shape is the same either way.
export const pageRef = pageId => `page:${pageId}`
export const occurrenceRef = occurrenceId => `occurrence:${occurrenceId}`

const parseRef = ref => {
  const [type, ...rest] = String(ref).split(":")
  return { type, id: rest.join(":") }
}

const writeProgress = (state, { ref, progress }) => {
  const { type, id } = parseRef(ref)
  const isOccurrence = type === "occurrence"
  const slice = isOccurrence ? state.occurrenceProgress : state.pageProgress
  const existing = slice[id] || {}
  const isCompleted = progress.is_completed ?? existing.is_completed ?? false

  Vue.set(slice, id, {
    ...existing,
    ...progress,
    is_completed: isCompleted,
    // An incomplete subject has no completion time.
    completed_at: isCompleted
      ? progress.completed_at ?? existing.completed_at ?? null
      : null
  })

  if (!isOccurrence) Vue.set(state.pageCompletions, id, isCompleted)
}

const resolveNextRequestByPageId = new Map()
const resolveNextSkipCompletedRequestByPageId = new Map()

// These Maps outlive the store's state, so `reset` disowns the previous
// member's resolve by bumping the session it committed under
let memberSession = 0

const commitBookmark = ({ commit, getters }, pageId, isBookmarked) => {
  if (getters.isPageBookmarked(pageId) !== isBookmarked) {
    commit("setPageIsBookmarked", { pageId, isBookmarked })
  }

  return isBookmarked
}

const progressArgs = payload =>
  typeof payload === "object" && payload !== null
    ? {
        pageId: payload.pageId,
        occurrence: payload.occurrence,
        completedAt: payload.completedAt
      }
    : { pageId: payload, occurrence: null }

const initialResolvedNextPagesState = () => ({
  resolvedNextPage: null,
  hasResolvedNextPage: false,
  isResolvingNextPage: false,

  resolvedNextSkipCompletedPage: null,
  hasResolvedNextSkipCompletedPage: false,
  isResolvingNextSkipCompletedPage: false
})

// Nothing in here is safe to show the next member on the device
const initialMemberState = () => ({
  ...initialResolvedNextPagesState(),
  pageCompletions: {},
  pageSequences: {},
  pageProgress: {},
  occurrenceProgress: {}, // Completion per occurrence, keyed by scheduled item id
  pageSubscriptions: {}, // Subscriptions from current user
  pageAccessGrants: {}, // Assigned private access from current user
  pageSubscribers: {}, // Count of _other_ subscribers for a page
  pageCanStartViaPack: {},
  bookmarkedPages: {},

  // Fetch gates, so the next member refetches
  isFetchingPageData: {},
  isFetchingAccessRoot: {},
  hasFetchedAccessRoot: {}
})

export default {
  namespaced: true,

  state: {
    currentPage: {},
    ambientOccurrence: null,
    pageData: {},
    pageAccess: {},
    pageTrees: {},
    toolbarHiddenPages: {},
    ...initialMemberState()
  },

  actions: {
    // Syncs Vuex with a server-returned `root_progress` envelope (the program
    // page id + its computed progress). Emitted by any endpoint that completes
    // a Page beneath a progress root.
    applyRootProgress({ commit }, rootProgress) {
      if (!rootProgress) return

      commit("setPageSubscription", rootProgress.page_id)
      commit("setPageProgress", {
        pageId: rootProgress.page_id,
        progress: rootProgress.progress
      })
    },

    completePage({ commit, dispatch }, payload) {
      const { pageId, occurrence, completedAt } = progressArgs(payload)
      const body = {}
      if (isPresent(occurrence)) body.occurrence = occurrence
      if (isPresent(completedAt)) body.completedAt = completedAt

      return ApiClient.post(`/pages/${pageId}/complete`, body).then(
        ({ data }) => {
          dispatch("applyRootProgress", data.root_progress)
          dispatch("markCompleted", {
            pageId: data.page_id,
            path: data.path,
            completedAt: data.completed_at
          })

          if (isPresent(occurrence)) {
            commit("setProgress", {
              ref: occurrenceRef(occurrence),
              progress: {
                is_completed: true,
                completed_at: data.completed_at
              }
            })
          }

          return data
        }
      )
    },

    markCompleted({ commit, getters }, { pageId, path, completedAt }) {
      // Emit only on a real transition so a repeat completion can't double-count.
      const isNewCompletion = !getters.isPageComplete(pageId)

      commit("setPageCompletion", { pageId, complete: true })
      commit("setPageProgress", {
        pageId,
        progress: { completed_at: completedAt }
      })

      if (isNewCompletion) {
        window.$mvt?.trackEvent(MVT_EVENTS.PROGRESS_COMPLETED, {
          path,
          completedAt
        })
      }
    },

    uncompletePage({ commit }, payload) {
      const { pageId, occurrence } = progressArgs(payload)
      const config = isPresent(occurrence)
        ? { params: { occurrence } }
        : undefined

      return ApiClient.delete(`/pages/${pageId}/complete`, config).then(() => {
        if (isPresent(occurrence)) {
          commit("setProgress", {
            ref: occurrenceRef(occurrence),
            progress: { is_completed: false, completed_at: null }
          })
        } else {
          commit("setPageCompletion", { pageId, complete: false })
        }
      })
    },

    subscribe({ commit, dispatch }, { pageId, startDate, startFromPageId }) {
      return ApiClient.post(`/pages/${pageId}/subscribers`, {
        started_at: startDate,
        start_from_page_id: startFromPageId
      }).then(res => {
        dispatch("applyRootProgress", {
          page_id: res.data.page_id,
          progress: res.data.progress
        })

        commit("setPageSequences", {
          pageId: res.data.page_id,
          data: {
            list: res.data.sequence
          }
        })

        res.data.sequence.forEach(page => {
          commit("setPageCompletion", {
            pageId: page.page_id,
            complete: page.is_completed
          })
        })

        return res.data
      })
    },

    unsubscribe({ dispatch, commit, state }, pageId) {
      return ApiClient.delete(`/pages/${pageId}/subscribers`).then(res => {
        const removedPageIds = Object.values(
          state.pageSequences[pageId]?.list || {}
        ).map(page => page.page_id)

        removedPageIds.forEach(pageId => {
          dispatch(
            "schedule/removeSubscriberSchedulesByItem",
            {
              itemId: pageId,
              itemType: "page"
            },
            {
              root: true
            }
          )
        })

        const clearedPageIds = [pageId, ...(res.data.uncompleted_pages || [])]
        clearedPageIds.forEach(clearedId => {
          commit("deletePageProgress", clearedId)
          commit("setPageCompletion", { pageId: clearedId, complete: false })
        })

        dispatch("resetPageSequences", pageId)
        commit("deletePageSubscription", pageId)

        return res.data
      })
    },

    fetchProgress({ commit }, payload) {
      const { pageId, occurrence } = progressArgs(payload)
      const config = isPresent(occurrence)
        ? { params: { occurrence } }
        : undefined

      return ApiClient.get(`/pages/${pageId}/complete`, config).then(res => {
        commit("setPageCompletion", {
          pageId,
          complete: res.data.is_completed
        })
        commit("setPageProgress", { pageId, progress: res.data })

        if (isPresent(res.data.occurrence)) {
          commit("setProgress", {
            ref: occurrenceRef(res.data.occurrence.id),
            progress: {
              is_completed: !!res.data.occurrence.completed,
              starts_at: res.data.occurrence.starts_at
            }
          })
        }

        return res.data
      })
    },

    resetPageSequences({ commit, state }, pageId) {
      const list = (state.pageSequences[pageId]?.list || []).map(s => ({
        ...s,
        is_completed: false
      }))

      commit("setPageSequences", { pageId, data: { list } })
    },

    togglePageBookmark(context, pageId) {
      return ApiClient.post(`/pages/${pageId}/bookmarks/toggle`).then(res =>
        commitBookmark(context, pageId, res.data.is_bookmarked)
      )
    },

    addPageBookmark(context, pageId) {
      return ApiClient.post(`/pages/${pageId}/bookmarks`).then(res =>
        commitBookmark(context, pageId, res.data.is_bookmarked)
      )
    },

    removePageBookmark(context, pageId) {
      return ApiClient.delete(`/pages/${pageId}/bookmarks`).then(res =>
        commitBookmark(context, pageId, res.data.is_bookmarked)
      )
    },

    fetchPageData({ dispatch, commit, state }, pageId) {
      if (state.isFetchingPageData[pageId]) return

      commit("setFetchingPageData", { pageId, fetching: true })

      return dispatch("fetchPageSubscribers", pageId).finally(() => {
        commit("setFetchingPageData", { pageId, fetching: false })
      })
    },

    // TODO: Rename to fetch page data
    fetchPageSubscribers({ commit }, pageId) {
      return ApiClient.get(`/pages/${pageId}/subscribers`).then(res => {
        commit("setPageSubscribers", {
          pageId: res.data.page_id,
          count: res.data.count,
          last_5: res.data.last_5,
          can_schedule_start: res.data.can_schedule_start
        })

        commit("setPageSequences", {
          pageId: res.data.page_id,
          data: {
            homePage: {
              title: res.data.page_title,
              path: res.data.page_path
            },
            list: res.data.sequence
          }
        })

        commit("setPageProgress", {
          pageId: res.data.page_id,
          progress: res.data.progress
        })

        res.data.sequence.forEach(page => {
          commit("setPageCompletion", {
            pageId: page.page_id,
            complete: page.is_completed
          })
        })

        return res.data
      })
    },

    fetchPageTree({ commit }, rootId) {
      const root = rootId ? rootId : ROOT_TREE_ID

      return ApiClient.get("/pages/tree", {
        params: {
          root: root === ROOT_TREE_ID ? null : root
        }
      }).then(res => {
        commit("setPageTree", { root, tree: res.data })

        res.data.forEach(page => {
          commit("setPageCompletion", {
            pageId: page.page_id,
            complete: page.is_completed
          })

          page.pages.forEach(page => {
            commit("setPageCompletion", {
              pageId: page.page_id,
              complete: page.is_completed
            })
          })
        })

        return res.data
      })
    },

    fetchPagePath(_, pageId) {
      return ApiClient.get(`/pages/${pageId}/path`).then(res => {
        return res.data.path
      })
    },

    resolveOpenNextPage(
      { commit, state },
      { pageId = null, skipCompleted = false } = {}
    ) {
      const retryAttempts = 3
      const retryDelayMs = 1000
      const currentPageId = pageId || state.currentPage?.id
      const inFlightRequests = skipCompleted
        ? resolveNextSkipCompletedRequestByPageId
        : resolveNextRequestByPageId

      if (!currentPageId) return Promise.resolve(null)

      const isResolvingKey = skipCompleted
        ? "isResolvingNextSkipCompletedPage"
        : "isResolvingNextPage"
      const hasResolvedKey = skipCompleted
        ? "hasResolvedNextSkipCompletedPage"
        : "hasResolvedNextPage"
      const resolvedKey = skipCompleted
        ? "resolvedNextSkipCompletedPage"
        : "resolvedNextPage"

      if (state[hasResolvedKey] && !state[isResolvingKey]) {
        return Promise.resolve(state[resolvedKey])
      }

      const inFlight = inFlightRequests.get(currentPageId)
      if (inFlight) return inFlight

      const session = memberSession

      commit("setIsResolvingNextPage", {
        isResolving: true,
        skipCompleted
      })

      const promise = (async () => {
        const maxAttempts = Math.max(1, retryAttempts)
        let res = null

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          res = await ApiClient.get(`/pages/${currentPageId}/resolve-next`, {
            quiet: true,
            params: {
              skip_completed: skipCompleted
            }
          }).catch(() => null)

          if (res) break

          if (attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, retryDelayMs))
          }
        }

        if (!res || session !== memberSession) return null

        const nextPage = res?.data?.next_page || null

        commit("setResolvedNextPage", { nextPage, skipCompleted })

        return nextPage
      })()
        .catch(() => null)
        .finally(() => {
          if (inFlightRequests.get(currentPageId) === promise) {
            inFlightRequests.delete(currentPageId)
          }

          if (session !== memberSession) return

          commit("setIsResolvingNextPage", {
            isResolving: false,
            skipCompleted
          })
        })

      inFlightRequests.set(currentPageId, promise)

      return promise
    },

    async loadAccessCacheIntoMemory({ commit }, rootId) {
      const accessTree = await getCachedAccessTree(rootId)

      if (isPresent(accessTree)) {
        Object.keys(accessTree).forEach(pageId => {
          commit("setPageAccess", {
            pageId: pageId,
            access: accessTree[pageId]
          })
        })

        return true
      } else {
        return true
      }
    },

    loadAccess(
      { dispatch, getters, rootGetters, state, commit },
      {
        pageId,
        rootId: knownRootId = null,
        isAwaitingAuthentication = false,
        retryCount = 0
      }
    ) {
      const setFetching = rootId => {
        commit("setIsFetchingAccessRoot", {
          pageId: rootId,
          isFetching: true
        })
      }

      const setResolved = rootId => {
        commit("setHasFetchedAccessRoot", {
          pageId: rootId,
          hasFetched: true
        })
        commit("setIsFetchingAccessRoot", {
          pageId: rootId,
          isFetching: false
        })
      }

      return new Promise(resolve => {
        // The cached page is read for its root alone, so a caller that already
        // knows it skips fetching the page to find out.
        const resolveRootId = isPresent(knownRootId)
          ? Promise.resolve(knownRootId)
          : getCachedPage(pageId).then(page => page?.root_id)

        resolveRootId
          .then(async rootId => {
            if (!rootId) return resolve() // Likely Legacy Content

            // If we have attempted to load the access tree too many times, we should just resolve
            if (retryCount >= LOAD_RETRY_LIMIT) {
              setResolved(rootId)

              return resolve()
            }

            if (
              !isAwaitingAuthentication &&
              state.isFetchingAccessRoot[rootId]
            ) {
              // Early return if there is already a fetch call being made
              // Importantly, we will not resolve early if we are waiting on the auth status to be resolved.
              // If we are waiting on that, we need to instead defer to the `hasCheckedAuthentication` below, which
              // may in turn re-call this method

              return setTimeout(async () => {
                // We will wait for 100ms, and then try again (which should resolve early)
                await dispatch("loadAccess", {
                  pageId,
                  rootId: knownRootId,
                  retryCount: retryCount + 1
                })

                return resolve()
              }, 100)
            }

            if (state.hasFetchedAccessRoot[rootId]) {
              // We can return early if another call has resolved and fetched the data
              return resolve()
            }

            // Ensure other callers know we are currently fetching the access tree
            setFetching(rootId)

            if (!getters.getPageAccess(rootId)) {
              await dispatch("loadAccessCacheIntoMemory", rootId)
            }

            // After loading from the cache, if we still don't have access data, we need to fetch it
            // and we will wait for the resolution
            if (!getters.getPageAccess(rootId)) {
              await dispatch("fetchAccessTree", rootId)
            } else {
              // We still want to get the most up to date data, but we can resolve early with the cached
              // data that is now in memory
              dispatch("fetchAccessTree", rootId)
            }

            const [accessRoot] = findAccessRoot({ pageId })

            if (
              !accessRoot ||
              !(accessRoot.is_scheduled || accessRoot.is_progress_root)
            ) {
              // If the page is not drip released or is a progress_root we can safely
              // resolve as we have all the data we need
              setResolved(rootId)

              return resolve()
            }

            // This is the special handling we need to do for Drip
            // We need to make an API call to fetch some data on the progress root (e.g. when it was started)
            // But, we need to ensure the PWA has checked the authentication status before we make the call
            // If we havn't checked the auth status, we need to wait and try again
            const progressRoot = findProgressRoot(pageId)

            if (!progressRoot) {
              // Resolving early here actually introduces a bug when the page previously didn't have
              // an progress root, but now does. Instead, we should actually re-check loadAccess after
              // the fresh data has been resolved by fetchAccessTree
              // Something like:
              //
              // await dispatch("fetchAccessTree", { pageId, isAwaitingFreshData: true })
              //
              setResolved(rootId)

              return resolve()
            }

            if (rootGetters["auth/hasCheckedAuthentication"]) {
              // If the page is part of drip release, we need to fetch the `started_at` date
              // from the server ONLY after we know if the user is logged in or not
              // Calling before `hasCheckedAuthentication` has been set will result in the
              // incorrect `started_at` date being fetched and the page being incorrectly locked
              await dispatch("fetchPageData", progressRoot)

              setResolved(rootId)

              return resolve()
            } else {
              setTimeout(async () => {
                // We will wait for 250ms, and then try again
                // We also tell `loadAccess` that we are waiting on auth data, so that it doesnt
                // return early and instead waits for the `hasCheckedAuthentication` to be set
                await dispatch("loadAccess", {
                  pageId,
                  rootId: knownRootId,
                  isAwaitingAuthentication: true,
                  retryCount: retryCount + 1
                })

                return resolve()
              }, 250)
            }
          })
          // Nothing rejects out of this action, so an unhandled throw anywhere in
          // the chain would leave every awaiting caller — the badge and the tap
          // alike — hanging instead of falling through to the app's global rules.
          .catch(() => resolve())
      })
    },

    fetchAccessTree({ commit }, rootId) {
      const url = joinUrl(
        process.env.VUE_APP_APP_DATA_PATH,
        "a",
        `${rootId}.json`,
        `?ts=${new Date().getTime()}`
      )

      return fetchWithRetry(url)
        .then(res => {
          commit("setAccessTree", { tree: res.data, rootId })
        })
        .catch(() => {
          // Swallowed rather than rethrown: the refresh above is
          // fire-and-forget, so a throw would be an unhandled rejection.
          // Clearing the flag loadAccess set before awaiting stops every later
          // load for this root burning the retry limit instead of fetching.
          commit("setIsFetchingAccessRoot", {
            pageId: rootId,
            isFetching: false
          })
        })
    }
  },

  mutations: {
    setFetchingPageData(state, { pageId, isFetching = true }) {
      Vue.set(state.isFetchingPageData, pageId, isFetching)
    },

    setIsFetchingAccessRoot(state, { pageId, isFetching = true }) {
      Vue.set(state.isFetchingAccessRoot, pageId, isFetching)
    },

    setHasFetchedAccessRoot(state, { pageId, hasFetched = true }) {
      Vue.set(state.hasFetchedAccessRoot, pageId, hasFetched)
    },

    setCurrentPage(
      state,
      {
        id,
        rootId,
        contentRootId,
        path,
        actions,
        access,
        type,
        title,
        data_fallbacks,
        currentSchedule,
        show_navigation,
        show_sidebar_navigation,
        show_tabbar,
        show_agent_launcher
      }
    ) {
      const currentId = state.currentPage?.id
      const isPageChange = currentId !== id

      state.currentPage = {
        id,
        rootId,
        contentRootId,
        path,
        actions,
        access,
        type,
        title,
        data_fallbacks,
        currentSchedule,
        show_navigation,
        show_sidebar_navigation,
        show_tabbar,
        show_agent_launcher
      }

      if (isPageChange) {
        Object.assign(state, initialResolvedNextPagesState())
      }
    },

    setResolvedNextPage(state, { nextPage, skipCompleted }) {
      if (skipCompleted) {
        state.resolvedNextSkipCompletedPage = nextPage
        state.hasResolvedNextSkipCompletedPage = true
      } else {
        state.resolvedNextPage = nextPage
        state.hasResolvedNextPage = true
      }
    },

    setIsResolvingNextPage(state, { isResolving, skipCompleted }) {
      if (skipCompleted) {
        state.isResolvingNextSkipCompletedPage = isResolving
      } else {
        state.isResolvingNextPage = isResolving
      }
    },

    setPageData(state, { pageId, data }) {
      Vue.set(state.pageData, pageId, data)
    },

    setPageCompletion(state, { pageId, complete }) {
      Vue.set(state.pageCompletions, pageId, complete)
    },

    setPageCompletions(state, pageCompletions) {
      state.pageCompletions = pageCompletions
    },

    setPageSubscriptions(state, pageSubscriptions) {
      state.pageSubscriptions = pageSubscriptions
    },

    setPageAccessGrants(state, pageAccessGrants) {
      state.pageAccessGrants = pageAccessGrants
    },

    setPageSubscription(state, pageId) {
      Vue.set(state.pageSubscriptions, pageId, true)
    },

    setPageSubscribers(state, { pageId, count, last_5, can_schedule_start }) {
      Vue.set(state.pageSubscribers, pageId, {
        count,
        last_5,
        can_schedule_start
      })
    },

    setPageSequences(state, { pageId, data }) {
      Vue.set(state.pageSequences, pageId, {
        ...(state.pageSequences[pageId] || {}),
        ...data
      })
    },

    setPageProgress(state, { pageId, progress }) {
      Vue.set(state.pageProgress, pageId, {
        ...(state.pageProgress[pageId] || {}),
        ...progress
      })
    },

    setProgress(state, entry) {
      writeProgress(state, entry)
    },

    setProgresses(state, entries) {
      entries.forEach(entry => writeProgress(state, entry))
    },

    // The day the member opened this page for. Held here rather than read off
    // the URL because a modal is a hash change that drops the query string.
    setAmbientOccurrence(state, { path, occurrenceId }) {
      if (isPresent(occurrenceId)) {
        state.ambientOccurrence = { path, id: String(occurrenceId) }
        return
      }

      if (state.ambientOccurrence?.path !== path) state.ambientOccurrence = null
    },

    setAccessTree(state, { rootId, tree }) {
      Object.keys(tree).forEach(pageId => {
        Vue.set(state.pageAccess, pageId, tree[pageId])
      })

      cacheAccessTree(rootId, tree)
    },

    setPageAccess(state, { pageId, access }) {
      Vue.set(state.pageAccess, pageId, access)
    },

    setPageCanStartViaPack(state, { pageId, canStartViaPack }) {
      state.pageCanStartViaPack = { [pageId]: canStartViaPack }
    },

    setBookmarkedPages(state, bookmarkedPages) {
      state.bookmarkedPages = bookmarkedPages
    },

    setPageIsBookmarked(state, { pageId, isBookmarked }) {
      Vue.set(state.bookmarkedPages, pageId, isBookmarked)
    },

    deletePageProgress(state, pageId) {
      Vue.delete(state.pageProgress, pageId)
    },

    deletePageSubscription(state, pageId) {
      Vue.delete(state.pageSubscriptions, pageId)
    },

    hidePageToolbar(state, pageId) {
      Vue.set(state.toolbarHiddenPages, pageId, true)
    },

    showPageToolbar(state, pageId) {
      Vue.set(state.toolbarHiddenPages, pageId, false)
    },

    setPageTree(state, { root, tree }) {
      Vue.set(state.pageTrees, root, tree)
    },

    reset(state) {
      memberSession++
      resolveNextRequestByPageId.clear()
      resolveNextSkipCompletedRequestByPageId.clear()

      Object.assign(state, initialMemberState())
    }
  },

  getters: {
    resolvedNextPage(state) {
      return state.resolvedNextPage
    },

    hasResolvedNextPage(state) {
      return state.hasResolvedNextPage
    },

    isResolvingNextPage(state) {
      return state.isResolvingNextPage
    },

    resolvedNextSkipCompletedPage(state) {
      return state.resolvedNextSkipCompletedPage
    },

    hasResolvedNextSkipCompletedPage(state) {
      return state.hasResolvedNextSkipCompletedPage
    },

    isResolvingNextSkipCompletedPage(state) {
      return state.isResolvingNextSkipCompletedPage
    },

    isPageComplete(state, _, __, rootGetters) {
      return pageId => {
        if (rootGetters["auth/isAuthenticated"]) {
          return !!state.pageCompletions[pageId]
        }

        return false
      }
    },

    getPageSequence(state) {
      return pageId => state.pageSequences[pageId] || {}
    },

    getPageProgress(state) {
      return pageId => state.pageProgress[pageId] || {}
    },

    getProgress(state) {
      return ref => {
        const { type, id } = parseRef(ref)
        if (type === "occurrence") return state.occurrenceProgress[id] || null

        const row = state.pageProgress[id]
        if (!row && !(id in state.pageCompletions)) return null

        // pageCompletions is the page's completion index; every writer keeps it.
        return { ...(row || {}), is_completed: !!state.pageCompletions[id] }
      }
    },

    getOccurrence(state) {
      return occurrenceId =>
        state.occurrenceProgress[String(occurrenceId)] || null
    },

    ambientOccurrenceId(state) {
      return path =>
        state.ambientOccurrence?.path === path
          ? state.ambientOccurrence.id
          : null
    },

    getPageAccess(state) {
      return page => state.pageAccess[page]
    },

    getCanStartViaPack(state) {
      return pageId => !!state.pageCanStartViaPack[pageId]
    },

    isSubscribedToPage(state) {
      return pageId => !!state.pageSubscriptions[pageId]
    },

    getPageSubscribers(state) {
      return pageId => state.pageSubscribers[pageId]
    },

    isPageBookmarked(state) {
      return pageId => !!state.bookmarkedPages[pageId]
    },

    isToolbarHidden(state) {
      return pageId => !!state.toolbarHiddenPages[pageId]
    },

    isLeftSidebarShown(state, _, rootState) {
      return (
        state.currentPage?.show_navigation &&
        state.currentPage?.show_sidebar_navigation &&
        rootState.responsive.isDesktop
      )
    },

    getPageData(state) {
      return pageId => state.pageData[pageId]
    },

    getPageTree(state) {
      return rootId => {
        const root = rootId ? rootId : ROOT_TREE_ID
        return state.pageTrees[root]
      }
    }
  }
}
